import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { apiUri } from '@/constants/Constants';
import { UserPermissions } from '@/constants/permissions.constant';
import { inviteEligibilityQueryKey } from '@/modules/application-tracker/api';
import { checkIfPmi, showSmbInvite } from '@/utils/commonUtils';
import { hasUserPermission, permissionIsNotSet } from '@/utils/permissions.util';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { type Renter, RenterInviteType } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';
import { InviteLeaseDetailsSegmentEvents } from '../constants';
import { useHasCustomScreeningStatusLabels } from './use-has-custom-screening-status-labels';
import { useInviteTracking } from './use-invite-tracking';
import { recordInviteEligibilitySubmitCompleted } from '../invite-eligibility.rum';
import {
  getInviteEligibilityPreflightOutcome,
  inviteEligibilityFinalOutcome,
  inviteSubmitErrorType,
} from '../utils/invite-eligibility-telemetry.util';
import { DealsDashboardClient } from '@/shared/api';
import {
  applyInviteServiceFieldErrors,
  buildEligibilityParamsFromRenter,
  buildInviteLeaseDetailsPayload,
  getEligibilityFromSubmitError,
  isInviteEligibilityBlockError,
  normalizeInviteLeaseDetailsValues,
  type SubmitErrorData,
} from '../utils';

const getUserType = (user: User) => {
  if (checkIfPmi(user)) {
    return 'PMI';
  }
  return user.LLType;
};

type UseInviteLeaseDetailsControllerOptions = {
  onSuccess?: (values: Renter) => void;
};

export const useInviteLeaseDetailsController = (
  options?: UseInviteLeaseDetailsControllerOptions,
) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const hasTriggeredOnLoadEventsRef = useRef(false);
  const { track } = useAnalyticsContext();
  const { trackInvite } = useInviteTracking();
  const queryClient = useQueryClient();

  const { user } = useCurrentUser();

  const isSmbInvite = useMemo(() => (user ? showSmbInvite(user) : false), [user]);
  const hasCustomScreeningStatusLabels = useHasCustomScreeningStatusLabels(isSmbInvite);

  /**
   * Usage of the new SMB form with dynamic coverages
   * Brought by DDS + Pricing Service
   */
  const useNewForm = useMemo(() => {
    /** PMI users are not eligible for the new SMB renter invite form */
    if (!user || checkIfPmi(user)) {
      return false;
    }

    /** If user is not an SMB user, return false */
    if (!isSmbInvite) {
      return false;
    }

    /** Any other case, return true. All SMB users (except PMI) are eligible for the new SMB renter invite form */
    return true;
  }, [user, isSmbInvite]);

  useEffect(() => {
    const shouldTriggerFirstLoadEvents =
      isSmbInvite && !hasTriggeredOnLoadEventsRef.current && Boolean(user);

    if (!shouldTriggerFirstLoadEvents) {
      return;
    }

    hasTriggeredOnLoadEventsRef.current = true;

    const inviteVariant = useNewForm
      ? RenterInviteType.SMBInviteNewLimitedSdr
      : RenterInviteType.SMBInviteOldCustomCoverage;

    track('Renter Invite form Visited', {
      newAbTests: { variant: inviteVariant },
      landlordType: getUserType(user),
      referrer: window.location.pathname,
    });
  }, [useNewForm, track, user, isSmbInvite]);

  const customCoverageDisabled = useMemo(() => {
    if (!user) {
      return false;
    }

    const isPermissionNotSet = permissionIsNotSet(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
    if (isPermissionNotSet) {
      return false;
    }

    return !hasUserPermission(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
  }, [user]);

  const trackServerValidationErrors = useCallback(
    (errors: Array<{ formField: string; message: string }>) => {
      if (errors.length === 0) {
        return;
      }
      trackInvite(InviteLeaseDetailsSegmentEvents.ValidationErrorsShown, {
        validation_source: 'server',
        error_category: 'server_validation',
        errors: errors.map(({ formField, message }) => ({
          field_name: formField,
          error_code: 'server_validation',
          error_message_shown: message,
        })),
      });
    },
    [trackInvite],
  );

  const onSubmit = useCallback(
    async (values: Renter, setError: UseFormSetError<Renter>, submitAttemptNumber?: number) => {
      setErrorMsg('');

      const normalizedValues = normalizeInviteLeaseDetailsValues(values, useNewForm, isSmbInvite);
      const requestPayload = buildInviteLeaseDetailsPayload(normalizedValues);

      const submitProps = {
        property_id: normalizedValues.address?.property_id,
        screening_label_id: normalizedValues.screeningStatus,
        has_custom_screening_status_labels: hasCustomScreeningStatusLabels(
          normalizedValues.address?.property_id,
        ),
        submit_attempt_number: submitAttemptNumber ?? 1,
      };

      const handleInviteServiceError = (data: SubmitErrorData | undefined) => {
        const eligibilityBlock = isInviteEligibilityBlockError(data);
        const eligibility = getEligibilityFromSubmitError(data);

        // Seed the preflight cache so Block banners appear even when preview said allow.
        if (eligibilityBlock && eligibility) {
          const params = buildEligibilityParamsFromRenter(normalizedValues);
          if (params) {
            queryClient.setQueryData(inviteEligibilityQueryKey(params), eligibility);
          }
        }

        if (data?.field_errors?.length) {
          const applied = applyInviteServiceFieldErrors(data.field_errors, setError, {
            treatRecipientEmailsAsEligibilityBlock: eligibilityBlock,
          });
          trackServerValidationErrors(applied);
          setErrorMsg('Please correct the errors above.');
          return;
        }

        setErrorMsg(data?.message || 'An error occurred while sending the invite.');
      };

      // Read before the request: a 409 block reseeds the check cache and changes the banners.
      const preflightOutcome = getInviteEligibilityPreflightOutcome('create');
      const trackSubmit = (failure?: AxiosError<SubmitErrorData>) => {
        const data = failure?.response?.data;
        const eligibilityBlocked = Boolean(failure) && isInviteEligibilityBlockError(data);
        const finalOutcome = inviteEligibilityFinalOutcome(eligibilityBlocked);
        const errorType = failure
          ? inviteSubmitErrorType({
              status: failure.response?.status,
              eligibilityBlocked,
              hasFieldErrors: Boolean(data?.field_errors?.length),
            })
          : undefined;
        trackInvite(InviteLeaseDetailsSegmentEvents.Submit, {
          ...submitProps,
          outcome: failure ? 'failure' : 'success',
          preflight_outcome: preflightOutcome,
          final_outcome: finalOutcome,
          ...(errorType ? { error_type: errorType } : {}),
        });
        recordInviteEligibilitySubmitCompleted('create', {
          preflightOutcome,
          finalOutcome,
          succeeded: !failure,
          errorType,
        });
      };

      try {
        await DealsDashboardClient.post(apiUri.inviteViaDealsService, requestPayload);
        trackSubmit();
        options?.onSuccess?.(values);
      } catch (error_) {
        const failure = error_ as AxiosError<SubmitErrorData>;
        trackSubmit(failure);
        handleInviteServiceError(failure.response?.data);
      }
    },
    [
      useNewForm,
      isSmbInvite,
      options,
      hasCustomScreeningStatusLabels,
      trackInvite,
      trackServerValidationErrors,
      queryClient,
    ],
  );

  return {
    user,
    errorMsg,
    isSmbInvite,
    useNewForm,
    customCoverageDisabled,
    onSubmit,
  };
};

export type UseInviteLeaseDetailsControllerResult = ReturnType<
  typeof useInviteLeaseDetailsController
>;
